import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing saved searches via the API.
 * 
 * @param {boolean} isSubscriber - Whether the current user is a Pro subscriber
 * @returns {Object} Saved searches state and operations
 */
export function useSavedSearches(isSubscriber) {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch saved searches from API
  const fetchSearches = useCallback(async () => {
    if (!isSubscriber) {
      setSearches([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/saved-searches', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          // Not a subscriber or not authenticated
          setSearches([]);
          return;
        }
        throw new Error('Failed to fetch saved searches');
      }

      const data = await response.json();
      setSearches(data.searches || []);
    } catch (err) {
      console.error('Error fetching saved searches:', err);
      setError(err.message);
      setSearches([]);
    } finally {
      setLoading(false);
    }
  }, [isSubscriber]);

  // Create a new saved search
  const saveSearch = useCallback(async (name, filters) => {
    if (!isSubscriber) {
      return { success: false, error: 'Pro subscription required' };
    }

    try {
      const response = await fetch('/api/saved-searches', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ name, filters }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to save search');
      }

      const data = await response.json();
      
      // Add new search to local state
      if (data.search) {
        setSearches(prev => [data.search, ...prev]);
      }

      return { success: true, search: data.search };
    } catch (err) {
      console.error('Error saving search:', err);
      return { success: false, error: err.message };
    }
  }, [isSubscriber]);

  // Update a saved search (rename or toggle notifications)
  const updateSearch = useCallback(async (id, updates) => {
    if (!isSubscriber) {
      return { success: false, error: 'Pro subscription required' };
    }

    try {
      const response = await fetch(`/api/saved-searches/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update search');
      }

      const data = await response.json();

      // Update local state
      if (data.search) {
        setSearches(prev => 
          prev.map(s => s.id === id ? data.search : s)
        );
      }

      return { success: true, search: data.search };
    } catch (err) {
      console.error('Error updating search:', err);
      return { success: false, error: err.message };
    }
  }, [isSubscriber]);

  // Delete a saved search
  const deleteSearch = useCallback(async (id) => {
    if (!isSubscriber) {
      return { success: false, error: 'Pro subscription required' };
    }

    try {
      const response = await fetch(`/api/saved-searches/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete search');
      }

      // Remove from local state
      setSearches(prev => prev.filter(s => s.id !== id));

      return { success: true };
    } catch (err) {
      console.error('Error deleting search:', err);
      return { success: false, error: err.message };
    }
  }, [isSubscriber]);

  // Mark a search as viewed (updates last_viewed_at)
  const markViewed = useCallback(async (id) => {
    if (!isSubscriber) {
      return { success: false, error: 'Pro subscription required' };
    }

    try {
      const response = await fetch(`/api/saved-searches/${id}/viewed`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark search as viewed');
      }

      const data = await response.json();

      // Update local state - reset new_projects_count to 0
      if (data.search) {
        setSearches(prev => 
          prev.map(s => s.id === id ? { ...s, ...data.search, new_projects_count: 0 } : s)
        );
      }

      return { success: true, search: data.search };
    } catch (err) {
      console.error('Error marking search as viewed:', err);
      return { success: false, error: err.message };
    }
  }, [isSubscriber]);

  // Load searches on mount when subscriber
  useEffect(() => {
    if (isSubscriber) {
      fetchSearches();
    }
  }, [isSubscriber, fetchSearches]);

  return {
    searches,
    loading,
    error,
    fetchSearches,
    saveSearch,
    updateSearch,
    deleteSearch,
    markViewed,
  };
}

export default useSavedSearches;
